/**
 * MirrorPage — Visao geral / Espelho do negocio.
 *
 * Fidelidade: prototipo visao-geral.html (B.48/B.49 F3).
 * Material 3 Expressive + Vivid Editorial CROSS.
 *
 * Estrutura:
 *   1. Page Header editorial (eyebrow, titulo partido, subtitulo orientador, meta)
 *   2. Bento hero 3 colunas:
 *      - Card roxo heroi (faturamento — dado real ou data-gap)
 *      - Card alcance (dado real ou data-gap)
 *      - Card investimento em anuncios (dado real ou data-gap)
 *      - Card saude do marketing (dado real ou data-gap)
 *   3. Destaques da semana (alerts reais mapeados para highlight-items)
 *   4. Atalhos de navegacao rapida
 *   5. FAB flutuante "Perguntar a CROSS"
 *   6. Modal de origem do dado
 *
 * BACKEND GAPs (marcados FLAG):
 *   FLAG-1: heroMessage ainda nao tem campos estruturados (valor, delta, fonte,
 *            periodo, nota) — MirrorSnapshot.kpis e a unica fonte de dado agora.
 *            Quando o backend entregar campos tipados por slot (revenue, reach,
 *            adSpend, healthScore), substituir o mapeamento em buildBentoData().
 *   FLAG-2: Destaques da semana (highlights) nao existem como endpoint proprio.
 *            Atualmente mapeado dos MirrorAlert existentes com degradacao honesta.
 *            Endpoint sugerido: GET /kpi/highlights?category=week
 *   FLAG-3: healthScore nao e retornado pela API atual — mostrado como data-gap.
 *            Endpoint sugerido: campo health_score no /kpi/snapshot.
 *   FLAG-4: Periodo e dados de coleta (lastCollectedAt) nao estao no snapshot.
 *            Sugerido: campo collected_at no /kpi/snapshot.
 *   FLAG-5: "Atalhos" mostram contagem dinamica de alertas ativos ("1 alerta ativo")
 *            — hardcoded como fallback; usar alerts.length quando disponivel.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient, ApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';
import { t } from '../lib/i18n';
import { SkeletonGrid } from '../components/Skeleton';
import { MirrorKpiModal, type MirrorKpiModalData } from '../components/MirrorKpiModal';
import '../styles/screen-mirror.css';

/* ================================================================
   TIPOS DA API
   ================================================================ */

interface MirrorKpi {
  readonly id: string;
  readonly title: string;
  readonly value: string | null;
  readonly unit?: string;
  readonly delta?: number | null;
}

interface MirrorSource {
  readonly id: string;
  readonly label: string;
  readonly status: 'connected' | 'disconnected' | 'error';
}

interface MirrorAlert {
  readonly id: string;
  readonly severity: 'critical' | 'warning' | 'ok';
  readonly message: string;
}

interface MirrorSnapshot {
  readonly heroMessage: string;
  readonly kpis: readonly MirrorKpi[];
  readonly sources: readonly MirrorSource[];
  readonly alerts: readonly MirrorAlert[];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: MirrorSnapshot }
  | { kind: 'error' };

/* ================================================================
   MAPEAMENTO DE DADOS PARA O BENTO
   Extrai slots nomeados dos kpis genericos.
   FLAG-1: quando o backend entregar campos por slot, simplificar aqui.
   ================================================================ */

interface BentoData {
  readonly revenue: MirrorKpi | null;
  readonly reach: MirrorKpi | null;
  readonly adSpend: MirrorKpi | null;
  readonly healthScore: MirrorKpi | null;
}

function buildBentoData(kpis: readonly MirrorKpi[]): BentoData {
  /* Tenta encontrar por id semantico, depois por titulo parcial (case-insensitive) */
  function find(ids: readonly string[], titles: readonly string[]): MirrorKpi | null {
    return (
      kpis.find((k) => ids.includes(k.id)) ??
      kpis.find((k) => titles.some((t) => k.title.toLowerCase().includes(t.toLowerCase()))) ??
      null
    );
  }

  return {
    revenue: find(['revenue', 'faturamento', 'receita'], ['faturamento', 'receita', 'revenue']),
    reach: find(['reach', 'alcance'], ['alcance', 'reach', 'pessoas']),
    adSpend: find(
      ['ad_spend', 'spend', 'investimento'],
      ['investimento', 'anuncio', 'spend', 'ads'],
    ),
    healthScore: find(['health_score', 'saude', 'health'], ['saude', 'health', 'score']),
  };
}

/* ================================================================
   FORMATADORES
   ================================================================ */

/**
 * Formata valor monetario em pt-BR com prefixo R$.
 * Se o valor ja vier prefixado ("R$ 1.274.054") retorna como esta.
 * Se vier numerico como string ("1274054" ou "1274054.32"), converte.
 * Se nao reconhecer como numerico, retorna o valor original intacto.
 */
function formatRevenue(raw: string): string {
  if (raw.startsWith('R$') || raw.startsWith('R ')) return raw;
  const cleaned = raw.replace(/\./g, '').replace(',', '.').trim();
  const num = Number(cleaned);
  if (!isNaN(num) && num > 0) {
    return num.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  return raw;
}

function formatDelta(delta: number | null | undefined): string | null {
  if (delta == null) return null;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(0)}%`;
}

function deltaClass(delta: number | null | undefined): string {
  if (delta == null) return 'mirror-delta-neutral';
  if (delta > 0) return 'mirror-delta-up';
  if (delta < 0) return 'mirror-delta-down';
  return 'mirror-delta-neutral';
}

function deltaIcon(delta: number | null | undefined): string {
  if (delta == null) return 'trending_flat';
  if (delta > 0) return 'trending_up';
  if (delta < 0) return 'trending_down';
  return 'trending_flat';
}

function currentPeriodLabel(): string {
  const now = new Date();
  const months = [
    'Janeiro',
    'Fevereiro',
    'Marco',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];
  return `${months[now.getMonth()]} ${now.getFullYear()}`;
}

function periodRange(): string {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `01/${month} a ${lastDay}/${month}/${year}`;
}

/* ================================================================
   COLD START / EMPTY
   ================================================================ */
function isColdStart(data: MirrorSnapshot): boolean {
  return data.kpis.length === 0;
}

/* ================================================================
   HIGHLIGHT TYPE — mapeia MirrorAlert para slot visual
   ================================================================ */
type HighlightType = 'pos' | 'warn' | 'info';

function alertToHighlightType(sev: MirrorAlert['severity']): HighlightType {
  if (sev === 'ok') return 'pos';
  if (sev === 'critical' || sev === 'warning') return 'warn';
  return 'info';
}

function alertIcon(sev: MirrorAlert['severity']): string {
  if (sev === 'ok') return 'trending_up';
  if (sev === 'critical') return 'error_outline';
  return 'warning_amber';
}

/* ================================================================
   MODAL DATA BUILDER — agrega metadados de fonte/periodo por kpi
   FLAG-4: quando API entregar collected_at, substituir placeholder.
   ================================================================ */
function buildModalData(kpi: MirrorKpi, sources: readonly MirrorSource[]): MirrorKpiModalData {
  const sourceName =
    sources.length > 0 ? sources.map((s) => s.label).join(' · ') : 'Fontes conectadas';

  return {
    title: kpi.title,
    value: kpi.value ?? '—',
    sourceName,
    account: '@seu_negocio' /* FLAG-4: substituir por tenant slug quando API entregar */,
    period: periodRange(),
    collectedAt: 'recentemente' /* FLAG-4: substituir por snapshot.collected_at */,
    note:
      kpi.value != null
        ? `${kpi.title} referente ao periodo ${periodRange()}.`
        : 'Dado nao disponivel para este periodo. A CROSS esta aguardando a proxima coleta.',
  };
}

/* ================================================================
   COMPONENTE PRINCIPAL
   ================================================================ */
export function MirrorPage(): JSX.Element {
  const { email, memberships, activeTenantId } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [modalData, setModalData] = useState<MirrorKpiModalData | null>(null);

  /* Ref para cancelar requests em flight ao desmontar */
  const cancelledRef = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    cancelledRef.current = false;
    setState({ kind: 'loading' });
    try {
      const data = await apiClient.get<MirrorSnapshot | null>('/kpi/snapshot?category=hero');
      if (cancelledRef.current) return;
      if (data === null || isColdStart(data)) {
        setState({ kind: 'empty' });
        return;
      }
      setState({ kind: 'ready', data });
    } catch (err) {
      if (cancelledRef.current) return;
      if (err instanceof ApiError && err.status === 404) {
        setState({ kind: 'empty' });
        return;
      }
      setState({ kind: 'error' });
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  /* Nome de exibicao: tenant ativo > e-mail > fallback */
  const activeMembership = memberships.find((m) => m.tenant_id === activeTenantId);
  const displayName = activeMembership?.tenant_name ?? email?.split('@')[0] ?? 'voce';

  /* Periodo atual */
  const periodLabel = currentPeriodLabel();

  /* ================================================================
     RENDER
     ================================================================ */
  return (
    <>
      <section className="mirror-root" aria-labelledby="mirror-title">
        {/* ---- PAGE HEADER ---- */}
        <div className="mirror-page-header" aria-hidden="false">
          <div>
            <div className="mirror-page-eyebrow">{periodLabel} — visao geral</div>
            <h1 id="mirror-title" className="mirror-page-title">
              O retrato
              <br />
              <em>de agora.</em>
            </h1>
            <p className="mirror-page-sub">
              Os numeros mais importantes do periodo — o que aconteceu.
              <br />
              <span style={{ fontSize: '12px', color: 'var(--md-outline)', fontStyle: 'italic' }}>
                Para o &ldquo;por que aconteceu e o que fazer&rdquo;, veja{' '}
                <Link to="/insights" className="mirror-page-sub a">
                  Insights
                </Link>
                .
              </span>
            </p>
          </div>
          <div className="mirror-page-meta">
            Dados de <strong>{periodRange()}</strong>
            <br />
            Fontes: Instagram · Meta Ads · Drive
          </div>
        </div>

        {/* ---- LOADING ---- */}
        {state.kind === 'loading' ? <SkeletonGrid count={4} /> : null}

        {/* ---- ERROR ---- */}
        {state.kind === 'error' ? (
          <div role="alert" className="mirror-error-block">
            <p>Nao conseguimos ler seu espelho agora. Tente novamente em instantes.</p>
            <button type="button" className="mirror-error-btn" onClick={() => void load()}>
              Tentar novamente
            </button>
          </div>
        ) : null}

        {/* ---- EMPTY / COLD START ---- */}
        {state.kind === 'empty' ? (
          <section className="mirror-cold-start" aria-live="polite">
            <span
              className="mirror-icon mirror-cold-start-icon"
              aria-hidden="true"
              style={{
                fontSize: '48px',
                fontVariationSettings: "'FILL' 0,'wght' 300,'GRAD' 0,'opsz' 48",
              }}
            >
              analytics
            </span>
            <h2>Coletando seus primeiros dados</h2>
            <p>
              Seus indicadores aparecem aqui em 24 a 72 horas — o tempo das suas fontes processarem
              a primeira coleta. Se ainda nao conectou nenhuma fonte, comece agora para acelerar
              esse prazo.
            </p>
            <Link to="/sources">Conectar fontes</Link>
          </section>
        ) : null}

        {/* ---- READY ---- */}
        {state.kind === 'ready' ? (
          <ReadyView
            data={state.data}
            displayName={displayName}
            onOpenModal={setModalData}
            onNavigate={(to) => navigate(to)}
          />
        ) : null}
      </section>

      {/* FAB flutuante — exibido sempre que autenticado */}
      <Link to="/chat" className="mirror-fab" aria-label={t('mirror.ctaChat')}>
        <span className="mirror-fab-icon mirror-icon" aria-hidden="true">
          forum
        </span>
        <span className="mirror-fab-label">{t('mirror.ctaChat')}</span>
      </Link>

      {/* Modal de origem do dado */}
      {modalData !== null ? (
        <MirrorKpiModal data={modalData} onClose={() => setModalData(null)} />
      ) : null}
    </>
  );
}

/* ================================================================
   SUBCOMPONENTE: READY VIEW
   Separa o conteudo "pronto" para evitar condicional aninhada.
   ================================================================ */
interface ReadyViewProps {
  readonly data: MirrorSnapshot;
  readonly displayName: string;
  readonly onOpenModal: (data: MirrorKpiModalData) => void;
  readonly onNavigate: (to: string) => void;
}

function ReadyView({
  data,
  displayName: _displayName,
  onOpenModal,
  onNavigate,
}: ReadyViewProps): JSX.Element {
  const bento = buildBentoData(data.kpis);

  return (
    <>
      {/* ---- INSIGHT PROATIVO (heroMessage) ---- */}
      {data.heroMessage.length > 0 ? (
        <MirrorInsightCard message={data.heroMessage} onNavigate={onNavigate} />
      ) : null}

      {/* ---- BENTO HERO ---- */}
      <div className="mirror-bento" role="region" aria-label="Indicadores principais do periodo">
        {/* CARD HEROI: Faturamento */}
        <MirrorHeroCard kpi={bento.revenue} sources={data.sources} onOpenModal={onOpenModal} />

        {/* CARD: Alcance */}
        <MirrorVgCard
          kpi={bento.reach}
          label="Alcance"
          sub="pessoas unicas"
          sourceType="ig"
          sources={data.sources}
          onOpenModal={onOpenModal}
        />

        {/* CARD: Investimento em anuncios */}
        <MirrorVgCard
          kpi={bento.adSpend}
          label="Investimento em anuncios"
          sub="Meta Ads · periodo completo"
          sourceType="ads"
          sources={data.sources}
          valueVariant="md"
          onOpenModal={onOpenModal}
        />

        {/* CARD: Saude do marketing — FLAG-3 */}
        <MirrorHealthCard
          kpi={bento.healthScore}
          sources={data.sources}
          onOpenModal={onOpenModal}
        />
      </div>

      {/* ---- DESTAQUES DA SEMANA ---- */}
      {/* FLAG-2: atualmente usa alerts reais; quando existir endpoint /kpi/highlights, substituir */}
      <section className="mirror-highlights" aria-labelledby="mirror-highlights-title">
        <div className="mirror-highlights-header">
          <div>
            <div className="mirror-editorial-kicker" aria-hidden="true">
              O CROSS identificou
            </div>
            <h2 id="mirror-highlights-title" className="mirror-editorial-headline">
              Destaques da semana
            </h2>
          </div>
          <p className="mirror-highlights-sub">O que merece atencao agora.</p>
        </div>

        <ul
          className="mirror-highlights-list"
          aria-label="Destaques e alertas da semana"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {data.alerts.length === 0 ? (
            <li>
              <MirrorHighlightItem
                type="info"
                icon="check_circle"
                text="Tudo sob controle por enquanto. Continue monitorando seus indicadores."
                actionLabel={null}
                sourceLabel={null}
                onAction={null}
              />
            </li>
          ) : (
            data.alerts.map((alert) => (
              <li key={alert.id}>
                <MirrorHighlightItem
                  type={alertToHighlightType(alert.severity)}
                  icon={alertIcon(alert.severity)}
                  text={alert.message}
                  actionLabel="Ver Radar de risco"
                  sourceLabel={null}
                  onAction={() => onNavigate('/radar')}
                />
              </li>
            ))
          )}
        </ul>
      </section>

      {/* ---- ATALHOS ---- */}
      <section className="mirror-shortcuts-section" aria-labelledby="mirror-shortcuts-title">
        <div style={{ marginBottom: '12px' }}>
          <div className="mirror-editorial-kicker" aria-hidden="true">
            Acesso rapido
          </div>
          <h2 id="mirror-shortcuts-title" className="mirror-editorial-headline">
            Onde ir agora
          </h2>
        </div>

        <div className="mirror-shortcuts-grid">
          <Link
            to="/dashboard"
            className="mirror-shortcut"
            aria-label="Painel completo — indicadores do periodo"
          >
            <span className="mirror-icon mirror-shortcut-icon" aria-hidden="true">
              bar_chart
            </span>
            <div className="mirror-shortcut-body">
              <div className="mirror-shortcut-label">Painel completo</div>
              <div className="mirror-shortcut-sub">Indicadores do periodo</div>
            </div>
            <span className="mirror-icon mirror-shortcut-arrow" aria-hidden="true">
              arrow_forward
            </span>
          </Link>

          <Link
            to="/insights"
            className="mirror-shortcut"
            aria-label="Descobertas do CROSS Flux — analise detalhada"
          >
            <span className="mirror-icon mirror-shortcut-icon" aria-hidden="true">
              lightbulb
            </span>
            <div className="mirror-shortcut-body">
              <div className="mirror-shortcut-label">Descobertas do CROSS Flux</div>
              <div className="mirror-shortcut-sub">Analise detalhada</div>
            </div>
            <span className="mirror-icon mirror-shortcut-arrow" aria-hidden="true">
              arrow_forward
            </span>
          </Link>

          <Link
            to="/radar"
            className="mirror-shortcut"
            aria-label={`Radar de risco — ${data.alerts.length > 0 ? `${data.alerts.length} alerta${data.alerts.length > 1 ? 's' : ''} ativo${data.alerts.length > 1 ? 's' : ''}` : 'tudo sob controle'}`}
          >
            <span className="mirror-icon mirror-shortcut-icon" aria-hidden="true">
              radar
            </span>
            <div className="mirror-shortcut-body">
              <div className="mirror-shortcut-label">Radar de risco</div>
              {/* FLAG-5: count de alertas ativos — usa data.alerts.length */}
              <div className="mirror-shortcut-sub">
                {data.alerts.length > 0
                  ? `${data.alerts.length} alerta${data.alerts.length > 1 ? 's' : ''} ativo${data.alerts.length > 1 ? 's' : ''}`
                  : 'Tudo sob controle'}
              </div>
            </div>
            <span className="mirror-icon mirror-shortcut-arrow" aria-hidden="true">
              arrow_forward
            </span>
          </Link>
        </div>
      </section>
    </>
  );
}

/* ================================================================
   SUBCOMPONENTE: INSIGHT PROATIVO (heroMessage editorial)
   Renderiza o heroMessage como card editorial com eyebrow pulsante
   e headline em Space Grotesk — fiel ao card-insight do prototipo.
   FLAG-1: quando o backend entregar campos estruturados (titulo,
   link de acao, fonte), substituir o conteudo do insight-text.
   ================================================================ */
interface MirrorInsightCardProps {
  readonly message: string;
  readonly onNavigate: (to: string) => void;
}

function MirrorInsightCard({ message, onNavigate }: MirrorInsightCardProps): JSX.Element {
  return (
    <article className="mirror-insight-card" aria-label={`Descoberta do CROSS Flux: ${message}`}>
      <div className="mirror-insight-eyebrow" aria-hidden="true">
        Descobertas do CROSS Flux
      </div>

      <p className="mirror-insight-text">{message}</p>

      <div className="mirror-insight-footer">
        <button
          type="button"
          className="mirror-hi-action"
          onClick={() => onNavigate('/insights')}
          aria-label="Ver analise completa nas Descobertas do CROSS Flux"
        >
          <span className="mirror-icon" aria-hidden="true" style={{ fontSize: '13px' }}>
            arrow_forward
          </span>
          Ver analise completa
        </button>
      </div>
    </article>
  );
}

/* ================================================================
   SUBCOMPONENTE: HERO CARD (faturamento)
   ================================================================ */
interface MirrorHeroCardProps {
  readonly kpi: MirrorKpi | null;
  readonly sources: readonly MirrorSource[];
  readonly onOpenModal: (data: MirrorKpiModalData) => void;
}

function MirrorHeroCard({ kpi, sources, onOpenModal }: MirrorHeroCardProps): JSX.Element {
  const hasData = kpi !== null && kpi.value !== null;
  const displayValue = hasData && kpi.value !== null ? formatRevenue(kpi.value) : null;
  const deltaStr = hasData ? formatDelta(kpi.delta) : null;
  const dIcon = hasData ? deltaIcon(kpi?.delta) : 'trending_flat';

  function handleOpen(): void {
    if (kpi !== null) {
      onOpenModal(buildModalData(kpi, sources));
    }
  }

  return (
    <article
      className="mirror-hero-card"
      onClick={hasData ? handleOpen : undefined}
      role={hasData ? 'button' : 'article'}
      tabIndex={hasData ? 0 : undefined}
      onKeyDown={
        hasData
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpen();
              }
            }
          : undefined
      }
      aria-label={
        hasData
          ? `${kpi.title}: ${displayValue}. Clique para ver a origem do dado.`
          : 'Faturamento do periodo — sem dado disponivel'
      }
    >
      {/* Circulo decorativo */}
      <div className="mirror-hero-card-circle" aria-hidden="true" />

      <div className="mirror-hero-inner">
        <div>
          <div className="mirror-hero-tag">Faturamento do periodo</div>

          {hasData ? (
            <>
              <span className="mirror-hero-value">{displayValue}</span>
              <p className="mirror-hero-label">receita no periodo</p>
            </>
          ) : (
            <>
              <span
                className="mirror-hero-value"
                style={{ fontFamily: 'var(--mkv-font-display)', fontSize: '48px', opacity: 0.45 }}
                aria-label="Dado nao disponivel"
              >
                —
              </span>
              <p className="mirror-hero-label" style={{ opacity: 0.65 }}>
                sem dado neste periodo
              </p>
              <span
                className="mirror-data-gap-chip"
                style={{ marginTop: '10px', display: 'inline-flex' }}
              >
                <span className="mirror-icon" aria-hidden="true" style={{ fontSize: '12px' }}>
                  warning_amber
                </span>
                aguardando coleta
              </span>
            </>
          )}
        </div>

        <div className="mirror-hero-bottom">
          {deltaStr !== null ? (
            <div
              className="mirror-hero-delta"
              aria-label={`Variacao: ${deltaStr} em relacao ao mes anterior`}
            >
              <span className="mirror-icon" aria-hidden="true">
                {dIcon}
              </span>
              {deltaStr} vs. mes anterior
            </div>
          ) : (
            <div
              className="mirror-hero-delta"
              aria-label="Variacao indisponivel"
              style={{ opacity: 0.5 }}
            >
              <span className="mirror-icon" aria-hidden="true">
                trending_flat
              </span>
              sem historico
            </div>
          )}

          {hasData ? (
            <div className="mirror-hero-src">
              via Planilhas do Drive
              <button
                type="button"
                className="mirror-hero-src-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen();
                }}
                aria-label="Ver origem do dado de faturamento"
              >
                ver origem
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/* ================================================================
   SUBCOMPONENTE: VG CARD (alcance, anuncios)
   ================================================================ */
type SourceType = 'ig' | 'ads' | 'cross';

interface MirrorVgCardProps {
  readonly kpi: MirrorKpi | null;
  readonly label: string;
  readonly sub: string;
  readonly sourceType: SourceType;
  readonly sources: readonly MirrorSource[];
  readonly valueVariant?: 'default' | 'md';
  readonly onOpenModal: (data: MirrorKpiModalData) => void;
}

const SOURCE_NAMES: Record<SourceType, string> = {
  ig: 'Instagram',
  ads: 'Meta Ads',
  cross: 'CROSS',
};

function MirrorVgCard({
  kpi,
  label,
  sub,
  sourceType,
  sources,
  valueVariant = 'default',
  onOpenModal,
}: MirrorVgCardProps): JSX.Element {
  const hasData = kpi !== null && kpi.value !== null;
  const deltaStr = hasData ? formatDelta(kpi.delta) : null;
  const dClass = hasData ? deltaClass(kpi.delta) : 'mirror-delta-neutral';
  const dIcon = hasData ? deltaIcon(kpi?.delta) : 'trending_flat';
  const sourceName = SOURCE_NAMES[sourceType];

  function handleOpen(): void {
    if (kpi !== null) {
      onOpenModal(buildModalData(kpi, sources));
    }
  }

  return (
    <article
      className="mirror-vg-card"
      onClick={hasData ? handleOpen : undefined}
      role={hasData ? 'button' : 'article'}
      tabIndex={hasData ? 0 : undefined}
      onKeyDown={
        hasData
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpen();
              }
            }
          : undefined
      }
      aria-label={
        hasData
          ? `${label}: ${kpi.value}. Clique para ver a origem do dado.`
          : `${label} — sem dado disponivel`
      }
    >
      <div className="mirror-vg-card-label">{label}</div>

      {hasData ? (
        <>
          <div
            className={`mirror-vg-card-value${valueVariant === 'md' ? ' mirror-vg-value-md' : ''}`}
          >
            {kpi.value}
          </div>
          <div className="mirror-vg-card-sub">{sub}</div>

          {deltaStr !== null ? (
            <div className={`mirror-vg-card-delta ${dClass}`} aria-label={`Variacao: ${deltaStr}`}>
              <span className="mirror-icon" aria-hidden="true" style={{ fontSize: '13px' }}>
                {dIcon}
              </span>
              {deltaStr}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div
            className="mirror-vg-card-value"
            style={{ color: 'var(--md-on-surface-variant)', opacity: 0.45 }}
            aria-label="Dado nao disponivel"
          >
            —
          </div>
          <div className="mirror-vg-card-sub" style={{ marginTop: 4 }}>
            <span className="mirror-data-gap-chip">
              <span className="mirror-icon" aria-hidden="true" style={{ fontSize: '12px' }}>
                warning_amber
              </span>
              sem dado neste periodo
            </span>
          </div>
        </>
      )}

      <div className="mirror-vg-card-footer">
        <span className="mirror-source-chip" aria-label={`Fonte: ${sourceName}`}>
          <span className={`mirror-src-dot mirror-dot-${sourceType}`} aria-hidden="true" />
          {sourceName}
        </span>

        {hasData ? (
          <button
            type="button"
            className="mirror-kpi-link"
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            aria-label={`Ver origem do dado de ${label}`}
          >
            ver origem
          </button>
        ) : null}
      </div>
    </article>
  );
}

/* ================================================================
   SUBCOMPONENTE: HEALTH CARD (saude do marketing)
   FLAG-3: healthScore ainda nao existe no endpoint — degradacao honesta.
   ================================================================ */
interface MirrorHealthCardProps {
  readonly kpi: MirrorKpi | null;
  readonly sources: readonly MirrorSource[];
  readonly onOpenModal: (data: MirrorKpiModalData) => void;
}

function MirrorHealthCard({ kpi, sources, onOpenModal }: MirrorHealthCardProps): JSX.Element {
  /* Tenta extrair score numerico do valor (ex: "85" ou "85/100") */
  const rawScore = kpi?.value?.replace('/100', '').trim() ?? null;
  const scoreNum = rawScore !== null ? parseInt(rawScore, 10) : null;
  const hasScore = scoreNum !== null && !isNaN(scoreNum);

  const healthFillPct = hasScore ? Math.min(Math.max(scoreNum, 0), 100) : 0;

  function handleOpen(): void {
    if (kpi !== null) {
      onOpenModal(buildModalData(kpi, sources));
    }
  }

  const subText = hasScore
    ? scoreNum >= 70
      ? 'Marketing saudavel'
      : scoreNum >= 40
        ? 'Atencao necessaria'
        : 'Risco alto — agir agora'
    : 'aguardando coleta';

  return (
    <article
      className="mirror-vg-card"
      onClick={hasScore ? handleOpen : undefined}
      role={hasScore ? 'button' : 'article'}
      tabIndex={hasScore ? 0 : undefined}
      onKeyDown={
        hasScore
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpen();
              }
            }
          : undefined
      }
      aria-label={
        hasScore
          ? `Saude do marketing: ${scoreNum} de 100. ${subText}`
          : 'Saude do marketing — aguardando coleta'
      }
    >
      <div className="mirror-vg-card-label">Saude do marketing</div>

      {hasScore ? (
        <>
          <div className="mirror-health-score" aria-label={`Score de saude: ${scoreNum} de 100`}>
            <span className="mirror-health-num">{scoreNum}</span>
            <span className="mirror-health-total">/100</span>
          </div>

          <div className="mirror-health-bar-wrap" aria-hidden="true">
            <div className="mirror-health-bar-track">
              <div
                className="mirror-health-bar-fill"
                style={{ width: `${healthFillPct}%` }}
                role="progressbar"
                aria-valuenow={healthFillPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Saude: ${healthFillPct}%`}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mirror-health-score" aria-label="Score de saude indisponivel">
            <span
              className="mirror-health-num"
              style={{ color: 'var(--md-on-surface-variant)', opacity: 0.45 }}
            >
              —
            </span>
            <span className="mirror-health-total">/100</span>
          </div>

          <div className="mirror-health-bar-wrap" aria-hidden="true">
            <div className="mirror-health-bar-track">
              <div className="mirror-health-bar-fill" style={{ width: '0%' }} />
            </div>
          </div>
        </>
      )}

      <div className="mirror-vg-card-sub" style={{ marginTop: '8px' }}>
        {subText}
        {/* FLAG-3: substituir por campo real quando API entregar health_score */}
        {!hasScore ? (
          <>
            {' '}
            <span className="mirror-data-gap-chip" style={{ marginLeft: '4px' }}>
              <span className="mirror-icon" aria-hidden="true" style={{ fontSize: '12px' }}>
                info
              </span>
              em breve
            </span>
          </>
        ) : null}
      </div>

      <div className="mirror-vg-card-footer">
        <span className="mirror-source-chip" aria-label="Fonte: CROSS">
          <span className="mirror-src-dot mirror-dot-cross" aria-hidden="true" />
          CROSS
        </span>

        {hasScore ? (
          <button
            type="button"
            className="mirror-kpi-link"
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            aria-label="Ver origem do score de saude"
          >
            ver origem
          </button>
        ) : (
          <Link to="/radar" className="mirror-kpi-link" aria-label="Ver Radar de risco">
            ver radar
          </Link>
        )}
      </div>
    </article>
  );
}

/* ================================================================
   SUBCOMPONENTE: HIGHLIGHT ITEM
   ================================================================ */
interface MirrorHighlightItemProps {
  readonly type: HighlightType;
  readonly icon: string;
  readonly text: string;
  readonly sourceLabel: string | null;
  readonly actionLabel: string | null;
  readonly onAction: (() => void) | null;
}

function MirrorHighlightItem({
  type,
  icon,
  text,
  sourceLabel,
  actionLabel,
  onAction,
}: MirrorHighlightItemProps): JSX.Element {
  return (
    <div className={`mirror-hi-item mirror-hi-${type}`} aria-atomic="true">
      <div className="mirror-hi-icon" aria-hidden="true">
        <span className="mirror-icon" style={{ fontSize: '18px' }}>
          {icon}
        </span>
      </div>

      <div className="mirror-hi-body">
        <p className="mirror-hi-text" style={{ margin: 0 }}>
          {text}
        </p>

        {sourceLabel !== null || actionLabel !== null ? (
          <div className="mirror-hi-meta">
            {sourceLabel !== null ? (
              <span className="mirror-kpi-link" style={{ pointerEvents: 'none' }}>
                {sourceLabel}
              </span>
            ) : null}

            {actionLabel !== null && onAction !== null ? (
              <button
                type="button"
                className="mirror-hi-action"
                onClick={onAction}
                aria-label={actionLabel}
              >
                <span className="mirror-icon" aria-hidden="true" style={{ fontSize: '13px' }}>
                  arrow_forward
                </span>
                {actionLabel}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
